// shared-components/context/ProductContext.jsx

import { createContext, useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import { useProductQueries } from "../hooks/useProductQueries";

// Create and export the context
// eslint-disable-next-line react-refresh/only-export-components
export const ProductContext = createContext(null);

export function ProductProvider({ children }) {
  // UI state (not cached)
  const [viewMode, setViewMode] = useState("grid");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    field: "name",
    direction: "asc",
  });
  const [filters, setFilters] = useState({
    searchQuery: "",
    priceRange: { min: 0, max: 1000 },
    inStockOnly: false,
    categories: [],
  });

  // Data fetching using React Query
  const {
    products: allProducts,
    categories,
    isLoading,
    error,
    priceRange,
    refetchAll,
  } = useProductQueries();

  // Update price range in filters when data loads
  useEffect(() => {
    if (
      allProducts.length > 0 &&
      (priceRange.min !== filters.priceRange.min ||
        priceRange.max !== filters.priceRange.max)
    ) {
      setFilters((prev) => ({
        ...prev,
        priceRange,
      }));
    }
  }, [
    priceRange,
    allProducts.length,
    filters.priceRange.min,
    filters.priceRange.max,
  ]);

  // Apply filters using useMemo to avoid unnecessary recalculations
  const filteredProducts = useMemo(() => {
    let filtered = [...allProducts];

    // Apply search filter
    if (filters.searchQuery) {
      filtered = filtered.filter(
        (product) =>
          product.name
            .toLowerCase()
            .includes(filters.searchQuery.toLowerCase()) ||
          product.description
            .toLowerCase()
            .includes(filters.searchQuery.toLowerCase()),
      );
    }

    // Apply price range filter
    if (filters.priceRange) {
      filtered = filtered.filter(
        (product) =>
          product.price >= filters.priceRange.min &&
          product.price <= filters.priceRange.max,
      );
    }

    // Apply stock filter
    if (filters.inStockOnly) {
      filtered = filtered.filter(
        (product) => product.in_stock && product.stock_quantity > 0,
      );
    }

    // Apply category filter
    if (filters.categories && filters.categories.length > 0) {
      filtered = filtered.filter((product) =>
        product.categories.some((cat) => filters.categories.includes(cat)),
      );
    }

    // Apply selected category filter (from category selector)
    if (selectedCategory) {
      filtered = filtered.filter((product) =>
        product.categories.includes(selectedCategory),
      );
    }

    return filtered;
  }, [allProducts, filters, selectedCategory]);

  // Apply sorting using useMemo
  const sortedProducts = useMemo(() => {
    const sorted = [...filteredProducts];

    sorted.sort((a, b) => {
      let aValue, bValue;

      // Handle special sort fields
      if (sortConfig.field === "popularity") {
        // Calculate popularity score: preview plays + (purchases × 10)
        // Note: popularity_data should be added to products by the data layer
        aValue = a.popularity_score ?? 0;
        bValue = b.popularity_score ?? 0;
      } else if (sortConfig.field === "created_at") {
        // Sort by date - handle ISO date strings
        aValue = a.created_at ? new Date(a.created_at).getTime() : 0;
        bValue = b.created_at ? new Date(b.created_at).getTime() : 0;
      } else {
        // Standard field sorting
        aValue = a[sortConfig.field];
        bValue = b[sortConfig.field];

        // Handle string sorting
        if (typeof aValue === "string") {
          aValue = aValue.toLowerCase();
          bValue = bValue?.toLowerCase() ?? "";
        }
      }

      if (aValue < bValue) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });

    return sorted;
  }, [filteredProducts, sortConfig]);

  // Filter functions
  const updateSearchQuery = (query) => {
    setFilters((prev) => ({ ...prev, searchQuery: query }));
  };

  const updatePriceRange = (range) => {
    setFilters((prev) => ({ ...prev, priceRange: range }));
  };

  const updateInStockOnly = (inStockOnly) => {
    setFilters((prev) => ({ ...prev, inStockOnly }));
  };

  const updateCategoryFilter = (categories) => {
    setFilters((prev) => ({ ...prev, categories }));
  };

  const clearFilters = () => {
    setFilters({
      searchQuery: "",
      priceRange: priceRange,
      inStockOnly: false,
      categories: [],
    });
    setSelectedCategory(null);
  };

  // Context value
  const value = {
    // Data (from React Query)
    products: sortedProducts,
    allProducts,
    categories,
    isLoading,
    error,

    // UI State
    viewMode,
    setViewMode,
    selectedCategory,
    setSelectedCategory,
    sortConfig,
    setSortConfig,
    filters,

    // Filter functions
    updateSearchQuery,
    updatePriceRange,
    updateInStockOnly,
    updateCategoryFilter,
    clearFilters,

    // Utility functions
    refreshData: refetchAll,
  };

  return (
    <ProductContext.Provider value={value}>{children}</ProductContext.Provider>
  );
}

ProductProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
